/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';

const useStyles = makeStyles(() => ({
  hourChooserWrapper : {
    maxWidth   : '150px',
    display    : 'flex',
    alignItems : 'center',
    whiteSpace : 'nowrap'
  },
  hourChooserInput : {
    width        : '100% !important',
    whiteSpace   : 'nowrap !important',
    textOverflow : 'ellipsis !important',
    overflow     : 'hidden !important',
    paddingRight : '1em !important'
  },
  hourChooserOption : {
    padding  : '6px 10px !important',
    fontSize : '14px !important'
  },
  hourChooserEndAdornment : {
    paddingRight : '2px !important'
  },
  hourChooserPopper : {
    position : 'absolute !important',
    zIndex   : 9999
  }
}));

type HourChooserProps = {
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  externalClasses?: {
    customDropdownInput?: string;
    optionOverride?: string;
    dropdownPopper?: string;
  };
};

const HourChooser: React.FC<HourChooserProps> = ({
  value,
  disabled,
  onChange,
  externalClasses = {}
}) => {
  const localClasses = useStyles();

  const options = useMemo(
    () =>
      Array.from({length: 24}, (_, i) => {
        const count = i + 1;
        const key = count === 1
          ? 'common:liveTracking.hourLabel'
          : 'common:liveTracking.hoursLabel';

        return {
          label : translate(key, {count}),
          value : count
        };
      }),
    []
  );

  const handleChange = useCallback((e, option) => {
    onChange(option?.value);
  }, [onChange]);

  return (
    <div className={localClasses.hourChooserWrapper}>
      <Dropdown options={options}
        value={value}
        placeholder={translate('common:liveTracking.selectHourPlaceholder')}
        fieldLabel=""
        displaySelectedLabel
        transparentBackground={false}
        disablePortal={false}
        externalClasses={{
          input  : classnames(localClasses.hourChooserInput, externalClasses.customDropdownInput),
          option : classnames(localClasses.hourChooserOption, externalClasses.optionOverride),
          popper : externalClasses.dropdownPopper
        }}
        popperProps={{
          className : classnames(localClasses.hourChooserPopper, externalClasses.dropdownPopper) // ✅ only className
        }}
        disabled={disabled}
        onChange={handleChange}/>
    </div>
  );
};

export default HourChooser;
